import { useEffect, useRef, useState, type RefObject } from 'react'

/**
 * The completeness meter, pinned under the navbar on phones.
 *
 * The form is two Bootstrap columns: the seven section cards in `col-md-8` and
 * the "Profile Completeness" card in `col-md-4`. Below the md breakpoint those
 * columns stack, so the card lands after every section — a user filling in
 * Education has no idea how far along they are without scrolling to the bottom
 * of the page and back.
 *
 * This is a mobile-only echo of that card's headline numbers. It hides itself
 * once the real card is on screen, so the two are never shown at once.
 *
 * Nothing here runs on desktop: the element is display:none from `md` up, and
 * `IntersectionObserver` on a hidden element simply never reports intersection,
 * which is harmless because the bar is unpainted either way.
 */
interface StickyProgressBarProps {
  percent: number
  completedCount: number
  totalCount: number
  /** The real card. While it is on screen this bar gets out of the way. */
  cardRef: RefObject<HTMLElement>
}

/** Fallback until the navbar is measured — it is `position: fixed` in landing.css. */
const DEFAULT_NAVBAR_HEIGHT = 72

export function StickyProgressBar({
  percent,
  completedCount,
  totalCount,
  cardRef,
}: StickyProgressBarProps): JSX.Element {
  const [isCardVisible, setIsCardVisible] = useState(false)
  const [navbarHeight, setNavbarHeight] = useState(DEFAULT_NAVBAR_HEIGHT)
  const [barHeight, setBarHeight] = useState(0)
  const barRef = useRef<HTMLDivElement>(null)

  /**
   * The bar is `position: fixed`, so it occupies no layout space and covers
   * whatever the page puts at the top — here, the "Personal Profile" heading.
   *
   * Reserving that space with a fixed padding does not work: how far the page
   * content already sits below the navbar depends on whether the navbar is in
   * flow, which AppLayout changes on scroll. A spacer in normal flow displaces
   * content by exactly the bar's height whatever the navbar is doing, and
   * measuring the bar keeps the two in step if its contents ever change.
   *
   * Above `md` the bar is `display: none`, so this measures 0 and the spacer
   * collapses on its own.
   */
  useEffect(() => {
    const bar = barRef.current
    if (!bar) return

    const measure = (): void => setBarHeight(bar.getBoundingClientRect().height)
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(bar)
    return () => observer.disconnect()
  }, [])

  /**
   * The navbar is fixed at top:0, so the bar has to start where it ends.
   * Measured rather than hard-coded: it is taller on phones (stacked brand +
   * toggler) than on desktop, and it changes height when `.top-nav-collapse`
   * is toggled on scroll by AppLayout.
   */
  useEffect(() => {
    const navbar = document.querySelector('.navbar')
    if (!navbar) return

    const measure = (): void => setNavbarHeight(navbar.getBoundingClientRect().height)
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(navbar)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const card = cardRef.current
    if (!card) return

    const observer = new IntersectionObserver(
      ([entry]) => setIsCardVisible(entry?.isIntersecting ?? false),
      // A sliver counts: the moment the card's top edge clears the bottom of
      // the viewport the user can see the real thing, so the echo retreats.
      { threshold: 0 },
    )
    observer.observe(card)
    return () => observer.disconnect()
  }, [cardRef])

  return (
    <>
      <div
        ref={barRef}
        className={`progress-sticky${isCardVisible ? ' progress-sticky--hidden' : ''}`}
        style={{ top: navbarHeight }}
        // Hidden from assistive tech while retreated; the real card carries the
        // same numbers and stays in the accessibility tree throughout.
        aria-hidden={isCardVisible}
      >
        <div className="progress-sticky__inner">
          <div className="progress-sticky__row">
            <span className="progress-sticky__title">Profile Completeness</span>
            <span className="progress-sticky__count">
              {completedCount} of {totalCount} · {percent}%
            </span>
          </div>
          <div className="progress-sticky__track">
            <div className="progress-sticky__fill" style={{ width: `${percent}%` }} />
          </div>
        </div>
      </div>

      {/* Keeps the page heading clear of the fixed bar — see the note above. */}
      <div className="progress-sticky__spacer" style={{ height: barHeight }} aria-hidden="true" />
    </>
  )
}
